import type { Rect, Strip } from "./streets.js";

/**
 * The street cross-section and the pieces around the asphalt, from the road strips (pure, unit
 * tested): segments between intersections (they carry the markings), junction squares, sidewalks
 * along both edges with a curb on the road side, corner squares that join the sidewalks around a
 * corner, and the crosswalks on every arm of an intersection. Meters, +X east, +Z south.
 *
 * Heights: the asphalt is crowned (CROWN at the centre line, 0 at the gutter), the gutter is a
 * strip of concrete at 0, the sidewalk top (and with it the parcel pads) is SIDEWALK_TOP, which
 * is also the curb height. Physics and the walker use the same numbers (`look/ground.ts`).
 */

export const SIDEWALK_TOP = 0.15;
export const SIDEWALK_W = 3;
export const CROWN = 0.05;
export const GUTTER_W = 0.45;
export const CURB_W = 0.22;
/** Crosswalk: starts this far outside the junction square and is this deep. */
export const CROSSWALK_GAP = 1.2;
export const CROSSWALK_DEPTH = 3.6;

/** Height of the road surface at `across` meters from the centre line of a road of half width `hw`. */
export function roadProfile(across: number, hw: number): number {
  const a = Math.abs(across);
  const inner = hw - GUTTER_W;
  if (a >= inner || inner <= 0) return 0;
  return CROWN * (1 - (a / inner) ** 1.6);
}

export interface Crossing {
  /** Index of the crossing road. */
  road: number;
  /** Coordinate along this road at the crossing's centre line, and the crossing road's half width. */
  at: number;
  half: number;
}

export interface Segment {
  road: number;
  alongZ: boolean;
  /** Centre line coordinate (x for a road along Z, z for one along X), half width. */
  center: number;
  hw: number;
  /** Extent along the road. */
  from: number;
  to: number;
  /** An intersection at the start or the end (crosswalk and stop line there). */
  startJunction: boolean;
  endJunction: boolean;
}

export interface Crosswalk {
  /** Centre, along which axis the road it crosses runs, width (across the road) and depth. */
  x: number;
  z: number;
  alongZ: boolean;
  width: number;
  depth: number;
}

export interface StreetPlan {
  segments: Segment[];
  junctions: Rect[];
  sidewalks: Rect[];
  /** Curbs (on the road side of the sidewalks), as thin rects. */
  curbs: (Rect & { alongZ: boolean })[];
  /** Corner squares joining the sidewalks of two streets. */
  corners: Rect[];
  crosswalks: Crosswalk[];
}

/** Where each road is crossed by another (sorted along the road). */
export function crossingsOf(roads: readonly Strip[]): Crossing[][] {
  const out: Crossing[][] = roads.map(() => []);
  roads.forEach((r, i) => {
    roads.forEach((o, j) => {
      if (o.alongZ === r.alongZ) return;
      const rc = r.alongZ ? r.x : r.z; // this road's centre line
      const oc = o.alongZ ? o.x : o.z; // the other's
      const rFrom = (r.alongZ ? r.z : r.x) - r.length / 2;
      const rTo = rFrom + r.length;
      const oFrom = (o.alongZ ? o.z : o.x) - o.length / 2;
      const oTo = oFrom + o.length;
      if (oc < rFrom || oc > rTo || rc < oFrom || rc > oTo) return;
      out[i]!.push({ road: j, at: oc, half: o.width / 2 });
    });
    out[i]!.sort((a, b) => a.at - b.at);
  });
  return out;
}

const rectAlong = (
  alongZ: boolean,
  center: number,
  a0: number,
  a1: number,
  c0: number,
  c1: number,
): Rect => {
  const along = (a0 + a1) / 2;
  const across = (c0 + c1) / 2;
  const la = Math.abs(a1 - a0);
  const lc = Math.abs(c1 - c0);
  return alongZ
    ? { x: center + across, z: along, w: lc, d: la }
    : { x: along, z: center + across, w: la, d: lc };
};

export function streetPlan(roads: readonly Strip[]): StreetPlan {
  const plan: StreetPlan = {
    segments: [],
    junctions: [],
    sidewalks: [],
    curbs: [],
    corners: [],
    crosswalks: [],
  };
  const crossings = crossingsOf(roads);
  roads.forEach((r, i) => {
    const center = r.alongZ ? r.x : r.z;
    const hw = r.width / 2;
    const from = (r.alongZ ? r.z : r.x) - r.length / 2;
    const to = from + r.length;
    // Free stretches between the crossings.
    let at = from;
    let startJunction = false;
    const cuts = crossings[i]!;
    const stretches: [number, number, boolean, boolean][] = [];
    for (const c of cuts) {
      const a = c.at - c.half;
      if (a - at > 1) stretches.push([at, a, startJunction, true]);
      at = Math.max(at, c.at + c.half);
      startJunction = true;
    }
    if (to - at > 1) stretches.push([at, to, startJunction, false]);
    for (const [a0, a1, sj, ej] of stretches) {
      plan.segments.push({
        road: i,
        alongZ: r.alongZ,
        center,
        hw,
        from: a0,
        to: a1,
        startJunction: sj,
        endJunction: ej,
      });
      // Sidewalks and curbs on both sides; at a junction they stop at the crossing road's edge.
      for (const side of [-1, 1] as const) {
        const c0 = side * hw;
        const c1 = side * (hw + SIDEWALK_W);
        plan.sidewalks.push(rectAlong(r.alongZ, center, a0, a1, c0, c1));
        plan.curbs.push({
          ...rectAlong(r.alongZ, center, a0, a1, c0, c0 + side * CURB_W),
          alongZ: r.alongZ,
        });
      }
    }
    // Junction squares (once, from the road along Z) and corner squares.
    if (r.alongZ) {
      for (const c of cuts) {
        plan.junctions.push({ x: center, z: c.at, w: r.width, d: c.half * 2 });
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            plan.corners.push({
              x: center + sx * (hw + SIDEWALK_W / 2),
              z: c.at + sz * (c.half + SIDEWALK_W / 2),
              w: SIDEWALK_W,
              d: SIDEWALK_W,
            });
          }
        }
      }
    }
    // Crosswalks on this road's arms of every intersection.
    for (const c of cuts) {
      for (const s of [-1, 1]) {
        const mid = c.at + s * (c.half + CROSSWALK_GAP + CROSSWALK_DEPTH / 2);
        if (mid < from || mid > to) continue;
        plan.crosswalks.push({
          x: r.alongZ ? center : mid,
          z: r.alongZ ? mid : center,
          alongZ: r.alongZ,
          width: r.width,
          depth: CROSSWALK_DEPTH,
        });
      }
    }
  });
  return plan;
}

/** Is a point on a rect (full width/depth, axis aligned)? */
export const inRect = (r: Rect, x: number, z: number, grow = 0): boolean =>
  Math.abs(x - r.x) <= r.w / 2 + grow && Math.abs(z - r.z) <= r.d / 2 + grow;
